import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Grid, IconButton, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import GridContainer from '../GridContainer';
import TextfieldWrapper from '../FormikWrappers/TextfieldWrapper';
import CheckBoxWrapper from '../FormikWrappers/CheckboxWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSafetyProjects } from '../../redux/actions/safety';
import '../../pages/Users/NewUser/newuser.scss';

const ChecklistFormDialog = ({ open, handleClose, handleOk, form, dialogTitle, onCreate, onUpdate }) => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const { safetyProjects } = useSelector(({ safety }) => safety);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    projectId: Yup.number().required('Project is required'),
    questions: Yup.array()
      .of(Yup.object().shape({ text: Yup.string().required('Question text is required') }))
      .min(1, 'At least one question is required')
  });

  const [initialFormState, setInitialFormState] = useState({
    title: '',
    projectId: '',
    isActive: true,
    questions: [{ text: '' }]
  });

  useEffect(() => {
    dispatch(fetchSafetyProjects({ page: 1, pageSize: 200, isActive: true }));
  }, []);

  useEffect(() => {
    if (form?.id) {
      setInitialFormState({
        title: form.title,
        projectId: form.safetyProject?.id || '',
        isActive: form.isActive,
        questions: form.questions?.length
          ? form.questions.map((q) => ({ text: q.text }))
          : [{ text: '' }]
      });
    }
  }, [form]);

  const submitForm = (values) => {
    const payload = {
      title: values.title,
      projectId: Number(values.projectId),
      questions: values.questions.map((q, index) => ({ text: q.text, sortOrder: index }))
    };
    if (form?.id) {
      dispatch(onUpdate(form.id, { ...payload, isActive: values.isActive }, handleOk));
    } else {
      dispatch(onCreate(payload, handleOk));
    }
  };

  return (
    <Dialog maxWidth="md" fullWidth onClose={handleClose} open={open}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        <Formik
          innerRef={formRef}
          initialValues={{ ...initialFormState }}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={submitForm}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <GridContainer className="newuser__container">
                <Grid item xs={12}>
                  <TextfieldWrapper name="title" label="Form Title" placeholder="Form Title" />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Safety Project</InputLabel>
                    <Select
                      value={values.projectId}
                      label="Safety Project"
                      onChange={({ target: { value } }) => setFieldValue('projectId', value)}
                    >
                      {(safetyProjects?.data || []).map((p) => (
                        <MenuItem value={p.id} key={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {touched.projectId && errors.projectId && (
                    <span style={{ color: 'red', fontSize: 12 }}>{errors.projectId}</span>
                  )}
                </Grid>
                {form?.id && (
                  <Grid item xs={12}>
                    <CheckBoxWrapper name="isActive" label="Active" />
                  </Grid>
                )}
                <FieldArray name="questions">
                  {({ push, remove }) => (
                    <>
                      {values.questions.map((_, index) => (
                        <Grid item xs={12} key={index}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TextfieldWrapper
                              name={`questions.${index}.text`}
                              label={`Question ${index + 1}`}
                              placeholder="Question text"
                            />
                            {values.questions.length > 1 && (
                              <IconButton onClick={() => remove(index)}>
                                <Delete />
                              </IconButton>
                            )}
                          </div>
                        </Grid>
                      ))}
                      <Grid item xs={12}>
                        <Button startIcon={<Add />} onClick={() => push({ text: '' })} color="primary">
                          Add Question
                        </Button>
                      </Grid>
                    </>
                  )}
                </FieldArray>
              </GridContainer>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => formRef.current.submitForm()} color="primary">
          Save
        </Button>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ChecklistFormDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleOk: PropTypes.func,
  form: PropTypes.object,
  dialogTitle: PropTypes.string,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default ChecklistFormDialog;
